import type { Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'

import { useTranslateFrameTypes } from '../../hooks/useTranslateFrameTypes'
import {
  isRangedValueFrameFieldValue,
  type SimpleValueSetupFrameValue,
} from '../../types/templateModel'
import { FPlusDiff } from '../fplusDiff/FPlusDiff'
import { DiffEntry } from './DiffEntry'
import { useHideInheritanceDiff } from './useHideInheritanceDiff'

type Props = {
  title: string
  current: Optional<SimpleValueSetupFrameValue>
  compare: Optional<SimpleValueSetupFrameValue>
}

/**
 * A frame holds one expression, whichever kind of frame it is: the range a value
 * must fall in, or the entries it may be chosen from. That expression is what
 * there is to read, so it is what is diffed.
 */
const frameExpression = (
  frame: Optional<SimpleValueSetupFrameValue>,
): Optional<string> => {
  if (!frame) {
    return undefined
  }

  return isRangedValueFrameFieldValue(frame)
    ? frame.range?.expression?.fplusSource
    : frame.entries?.expression?.fplusSource
}

/**
 * A field's frame, read as the kind of frame it is and a diff of the expression
 * that defines it.
 *
 * The kind is a short value rather than code, so it keeps the
 * before-beside-after presentation, and it is only shown when it changed. It is
 * asked about separately from the expression because a frame can be given a
 * kind before anything is written into it, and because the same text means
 * different things as a range and as a list of entries: leaving the kind to the
 * expression alone would hide both.
 */
export const SimpleValueSetupFrameValueDiff = ({
  current,
  compare,
  title,
}: Props) => {
  const translate = useTranslate()
  const frameTypeTranslations = useTranslateFrameTypes()
  const isHidingInheritanceDiff = useHideInheritanceDiff(current)

  if (isHidingInheritanceDiff) {
    return null
  }

  return (
    <>
      {compare?.type !== current?.type && (
        <DiffEntry
          title={translate('diffPopover.field.frameType')}
          compare={frameTypeTranslations[compare?.type ?? 'none']}
          current={frameTypeTranslations[current?.type ?? 'none']}
        />
      )}
      <FPlusDiff
        title={title}
        compare={frameExpression(compare)}
        current={frameExpression(current)}
      />
    </>
  )
}
